/**
 * Feature Tooltip Component - Contextual Help & Discovery
 * Version: 6.2.0
 * 
 * Features:
 * - Hover tooltips with feature info
 * - Keyboard shortcut hints
 * - Feature discovery panel
 * - Guided tours
 */
import { useState, useRef, useEffect, ReactNode } from 'react'
import './FeatureTooltip.css'

interface FeatureInfo {
  title: string
  description: string
  tip?: string
  shortcut?: string
  category?: string
  path?: string
}

interface FeatureTooltipProps {
  children: ReactNode
  feature: FeatureInfo
  position?: 'top' | 'bottom' | 'left' | 'right'
  delay?: number
  showOnce?: boolean
  id?: string
}

function FeatureTooltip({ 
  children, 
  feature, 
  position = 'top',
  delay = 300,
  showOnce = true,
  id
}: FeatureTooltipProps) {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const timeoutRef = useRef<number>()
  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (showOnce && id) {
      const dismissedFeatures = JSON.parse(localStorage.getItem('dismissed_tooltips') || '[]')
      if (dismissedFeatures.includes(id)) {
        setDismissed(true)
      }
    }
  }, [id, showOnce])

  const showTooltip = () => {
    if (dismissed) return
    timeoutRef.current = window.setTimeout(() => {
      setVisible(true)
    }, delay)
  }

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setVisible(false)
  }

  const dismissTooltip = () => {
    if (id) {
      const dismissedFeatures = JSON.parse(localStorage.getItem('dismissed_tooltips') || '[]')
      dismissedFeatures.push(id)
      localStorage.setItem('dismissed_tooltips', JSON.stringify(dismissedFeatures))
    }
    setDismissed(true)
    setVisible(false)
  }

  if (dismissed) {
    return <div ref={elementRef}>{children}</div>
  }

  return (
    <div 
      ref={elementRef}
      className="feature-tooltip-wrapper"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      
      {visible && (
        <div className={`feature-tooltip feature-tooltip-${position}`}>
          <div className="tooltip-content">
            <div className="tooltip-header">
              <span className="tooltip-icon">💡</span>
              <span className="tooltip-title">{feature.title}</span>
              <button className="tooltip-dismiss" onClick={dismissTooltip}>✕</button>
            </div>
            <p className="tooltip-description">{feature.description}</p>
            {feature.tip && (
              <div className="tooltip-tip">
                <span className="tip-label">Pro tip:</span>
                <span>{feature.tip}</span>
              </div>
            )}
            {feature.shortcut && (
              <div className="tooltip-shortcut">
                <span>Shortcut:</span>
                <kbd>{feature.shortcut}</kbd>
              </div>
            )}
          </div>
          <div className="tooltip-arrow" />
        </div>
      )}
    </div>
  )
}

// Feature Discovery Tour Component
interface TourStep {
  target: string
  title: string
  description: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}

interface FeatureTourProps {
  steps: TourStep[]
  onComplete: () => void
  onSkip: () => void
}

function FeatureTour({ steps, onComplete, onSkip }: FeatureTourProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    const step = steps[currentStep]
    const element = document.querySelector(step.target)
    if (element) {
      const rect = element.getBoundingClientRect()
      setPosition({ top: rect.top + rect.height / 2, left: rect.left + rect.width / 2 })
    }
  }, [currentStep, steps])

  const goNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      onComplete()
    }
  }

  const goPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  return (
    <div className="feature-tour-overlay">
      <div className="feature-tour-spotlight" style={{ top: position.top, left: position.left }} />
      <div 
        className={`feature-tour-card tour-${steps[currentStep].position || 'top'}`}
        style={{ top: position.top, left: position.left }}
      >
        <div className="tour-progress">
          {steps.map((_, i) => (
            <span 
              key={i} 
              className={`progress-dot ${i <= currentStep ? 'active' : ''}`}
            />
          ))}
        </div>
        <h3 className="tour-title">{steps[currentStep].title}</h3>
        <p className="tour-description">{steps[currentStep].description}</p>
        <div className="tour-actions">
          <button className="tour-skip" onClick={onSkip}>Skip Tour</button>
          <div className="tour-nav">
            {currentStep > 0 && (
              <button className="tour-prev" onClick={goPrev}>← Back</button>
            )}
            <button className="tour-next" onClick={goNext}>
              {currentStep < steps.length - 1 ? 'Next →' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Quick Feature Cards for Discovery
interface QuickFeatureCardProps {
  icon: string
  title: string
  description: string
  action: () => void
}

function QuickFeatureCard({ icon, title, description, action }: QuickFeatureCardProps) {
  const [hovered, setHovered] = useState(false)

  return (
    <button 
      className={`quick-feature-card ${hovered ? 'hovered' : ''}`}
      onClick={action}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="card-icon">{icon}</div>
      <div className="card-content">
        <h4 className="card-title">{title}</h4>
        <p className="card-desc">{description}</p>
      </div>
      <div className="card-arrow">→</div>
      <div className="card-shine" />
    </button>
  )
}

// Keyboard Shortcuts Reference Component
function KeyboardShortcuts() {
  const [showShortcuts, setShowShortcuts] = useState(false)

  const shortcuts = [
    { key: 'Ctrl + K', description: 'Open Command Palette' },
    { key: 'Ctrl + /', description: 'Open Navigation Guide' },
    { key: 'Esc', description: 'Close modals/popups' },
    { key: '↑ ↓', description: 'Navigate up/down in lists' },
    { key: 'Enter', description: 'Select/confirm action' },
    { key: 'G then D', description: 'Go to Dashboard' },
    { key: 'G then S', description: 'Go to Strategies' },
    { key: 'G then B', description: 'Go to Brokers' },
    { key: 'N', description: 'New strategy' },
    { key: 'P', description: 'Toggle paper trading' },
  ]

  return (
    <div className="keyboard-shortcuts">
      <button 
        className="shortcuts-toggle"
        onClick={() => setShowShortcuts(!showShortcuts)}
        title="Keyboard Shortcuts"
      >
        <span>⌨️</span>
        <span>Shortcuts</span>
      </button>
      
      {showShortcuts && (
        <div className="shortcuts-panel">
          <div className="shortcuts-header">
            <h3>⌨️ Keyboard Shortcuts</h3>
            <button onClick={() => setShowShortcuts(false)}>✕</button>
          </div>
          <div className="shortcuts-list">
            {shortcuts.map((shortcut, i) => (
              <div key={i} className="shortcut-item">
                <kbd className="shortcut-key">{shortcut.key}</kbd>
                <span className="shortcut-desc">{shortcut.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Feature Discovery Panel
interface FeatureDiscoveryProps {
  onClose: () => void
}

function FeatureDiscovery({ onClose }: FeatureDiscoveryProps) {
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  const categories = [
    { id: 'all', label: 'All Features', icon: '✨' },
    { id: 'navigation', label: 'Navigation', icon: '🧭' },
    { id: 'trading', label: 'Trading', icon: '📈' },
    { id: 'analysis', label: 'Analysis', icon: '🔍' },
    { id: 'automation', label: 'Automation', icon: '🤖' },
    { id: 'shortcuts', label: 'Shortcuts', icon: '⌨️' },
  ]

  const features = [
    { 
      id: 'ctrl-k', 
      icon: '⌘', 
      title: 'Command Palette', 
      description: 'Press Ctrl+K anywhere to quickly search features and navigate',
      category: 'navigation',
      shortcut: 'Ctrl+K'
    },
    { 
      id: 'broker-connect', 
      icon: '🔗', 
      title: 'Broker Connect', 
      description: 'Connect to 82+ broker adapters including Zerodha, Angel One, Upstox',
      category: 'trading'
    },
    { 
      id: 'ai-strategy', 
      icon: '🧠', 
      title: 'AI Strategy Builder', 
      description: 'Describe your trading idea in plain English and let AI build it',
      category: 'automation'
    },
    { 
      id: 'backtest', 
      icon: '📊', 
      title: 'Backtesting', 
      description: 'Test your strategies against historical data with detailed reports',
      category: 'analysis'
    },
    { 
      id: 'multi-timeframe', 
      icon: '📅', 
      title: 'Multi-Timeframe Analysis', 
      description: 'Analyze across 1m, 5m, 15m, 1h, 4h, 1D timeframes',
      category: 'analysis'
    },
    { 
      id: 'paper-trade', 
      icon: '📝', 
      title: 'Paper Trading', 
      description: 'Practice strategies without risking real money',
      category: 'trading'
    },
    { 
      id: 'notifications', 
      icon: '🔔', 
      title: 'Smart Notifications', 
      description: 'Get alerts via Telegram, SMS, or email for trade signals',
      category: 'automation'
    },
    { 
      id: 'portfolio', 
      icon: '💼', 
      title: 'Portfolio Analytics', 
      description: 'Track your P&L, positions, and performance metrics',
      category: 'analysis'
    },
  ]

  // Filter features by category and search
  const filteredFeatures = features.filter(feature => {
    const matchesCategory = activeCategory === 'all' || feature.category === activeCategory
    const matchesSearch = searchTerm === '' || 
      feature.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.description.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  // Keyboard shortcuts reference for shortcuts category
  const keyboardShortcuts = [
    { key: 'Ctrl + K', description: 'Open Command Palette' },
    { key: 'Ctrl + /', description: 'Navigation Guide' },
    { key: 'Esc', description: 'Close modals' },
    { key: '↑ ↓', description: 'Navigate lists' },
    { key: 'Enter', description: 'Select/confirm' },
    { key: 'G then D', description: 'Go to Dashboard' },
    { key: 'G then S', description: 'Go to Strategies' },
    { key: 'G then B', description: 'Go to Brokers' },
    { key: 'N', description: 'New strategy' },
    { key: 'P', description: 'Toggle paper trading' },
  ]

  return (
    <div className="feature-discovery-overlay" onClick={onClose}>
      <div className="feature-discovery-panel" onClick={e => e.stopPropagation()}>
        <div className="panel-header">
          <h2>✨ Discover Features</h2>
          <button className="panel-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="panel-search">
          <input
            type="text"
            placeholder="Search features..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="panel-categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        <div className="panel-features">
          {activeCategory === 'shortcuts' ? (
            // Show keyboard shortcuts
            <div className="shortcuts-reference">
              {keyboardShortcuts.map((shortcut, i) => (
                <div key={i} className="shortcut-row">
                  <kbd className="shortcut-key">{shortcut.key}</kbd>
                  <span className="shortcut-desc">{shortcut.description}</span>
                </div>
              ))}
            </div>
          ) : filteredFeatures.length > 0 ? (
            // Show filtered features
            filteredFeatures.map(feature => (
              <div key={feature.id} className="feature-item">
                <span className="feature-icon">{feature.icon}</span>
                <div className="feature-info">
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
                {feature.shortcut && (
                  <kbd className="feature-shortcut">{feature.shortcut}</kbd>
                )}
              </div>
            ))
          ) : (
            <div className="no-results">
              <span>🔍</span>
              <p>No features found</p>
            </div>
          )}
        </div>

        <div className="panel-footer">
          <span>💡 Tip: Press Ctrl+K anytime to open the command palette</span>
        </div>
      </div>
    </div>
  )
}

export { FeatureTooltip, FeatureTour, QuickFeatureCard, FeatureDiscovery, KeyboardShortcuts }
export type { TourStep, FeatureInfo }